/**
 * Impersonation banner — rendered at the very top of the SPA when
 * the active session cookie carries an `imp_admin_id` claim.
 *
 * The banner is intentionally loud (red, full-width, sticky) so an
 * admin can't accidentally forget they're acting as another user.
 *
 * "End impersonation" hits /admin/api/impersonate/end which re-
 * issues the admin's normal session cookie + reloads.
 */

import { useEffect, useState } from 'react'

type Status =
  | { active: false }
  | {
      active: true
      imp_admin_id: string
      imp_admin_email: string
      target_user_id: string
      expires_at?: number
    }

export default function ImpersonationBanner() {
  const [status, setStatus] = useState<Status>({ active: false })
  const [ending, setEnding] = useState(false)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const r = await fetch('/v1/auth/impersonation-status', {
          credentials: 'include',
        })
        if (!r.ok) return
        const d = await r.json()
        if (!cancelled) setStatus(d)
      } catch {
        /* keep banner hidden on failure */
      }
    }
    load()
    // Re-check every 60s — token expires after 60 min so we want to
    // hide the banner promptly if the cookie was cleared elsewhere.
    const t = setInterval(load, 60_000)
    return () => {
      cancelled = true
      clearInterval(t)
    }
  }, [])

  const end = async () => {
    setEnding(true)
    try {
      await fetch('/admin/api/impersonate/end', {
        method: 'POST',
        credentials: 'include',
      })
    } finally {
      // Hard reload — easiest way to flush any in-memory user state
      // that was pulled while impersonated.
      window.location.reload()
    }
  }

  if (!status.active) return null

  const expiresLabel = status.expires_at
    ? new Date(status.expires_at * 1000).toLocaleTimeString()
    : ''

  return (
    <div
      role="alert"
      className="sticky top-0 z-50 bg-rose-600 text-white text-sm shadow-md"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex flex-wrap items-center gap-3">
        <span className="text-base">🛑</span>
        <div className="flex-1 min-w-0">
          <div className="font-semibold">
            Impersonating user — view-as session active
          </div>
          <div className="text-rose-100 text-xs">
            started by <span className="font-mono">{status.imp_admin_email}</span>
            {expiresLabel ? ` · expires ${expiresLabel}` : ''}
          </div>
        </div>
        <button
          onClick={end}
          disabled={ending}
          className="px-3 py-1 bg-white text-rose-700 text-xs font-semibold rounded-md hover:bg-rose-50 disabled:opacity-60 transition-colors"
        >
          {ending ? 'Ending…' : 'End impersonation'}
        </button>
      </div>
    </div>
  )
}
